import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FRANCHISE_FORM_ENDPOINT,
  STATES,
  GENDERS,
  CATEGORIES,
  SKILLS,
  DISTRICTS_BY_STATE,
  DECLARATION_VALUE,
} from "./formData";
import "../../styles/franchise.css";

const OTHER_SKILL = "Other / अन्य";

const Label = ({ en, hi }) => (
  <label className="fr-label">
    {en}
    <span className="fr-label-hi"> / {hi}</span>
  </label>
);

const SectionHead = ({ n, en, hi }) => (
  <div className="fr-section-head">
    <h3 className="fr-section-title">
      {n}. {en} <span className="fr-section-title-hi">/ {hi}</span>
    </h3>
  </div>
);


const OptionList = ({ options, name, register, type = "radio", rules, grid }) => (
  <div className={grid ? "fr-options--grid" : "fr-options"}>
    {options.map(([en, hi]) => {
      const value = hi ? `${en} / ${hi}` : en;
      return (
        <label className="fr-option" key={value}>
          <input type={type} value={value} {...register(name, rules)} />
          {value}
        </label>
      );
    })}
  </div>
);

const FranchiseForm = () => {
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({ mode: "onTouched" });

  const skills = watch("skills") || [];
  const state = watch("state");
  const districts = DISTRICTS_BY_STATE[state] || [];


  useEffect(() => {
    setValue("district", "");
  }, [state, setValue]);

  const onSubmit = async (data) => {
    setSubmitError("");

    const payload = {
      email: data.email,
      fullName: data.fullName,
      guardianName: data.guardianName || "",
      whatsappNumber: data.whatsappNumber,
      dob: data.dob,
      gender: data.gender,
      category: data.category,
      state: data.state,
      district: data.district,
      pincode: data.pincode,
      skills: data.skills,
      otherSkillDescription: data.otherSkillDescription || "",
      declarationAgreed: !!data.declarationAgreed,
    };

    try {
      const response = await fetch(FRANCHISE_FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (result.status !== "success") {
        throw new Error(result.message || "Submission failed");
      }

      setSuccess(true);
      reset();
    } catch (err) {
      setSubmitError(
        "Something went wrong while submitting. Please try again. / सबमिट करते समय कुछ गड़बड़ हुई। कृपया पुनः प्रयास करें।"
      );
    }
  };

  return (
    <section className="fr fr-form-section">
      <div className="container">
        <div className="fr-card">
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* SECTION 1 */}
            <div className="fr-section">
              <SectionHead n={1} en="Personal Details" hi="व्यक्तिगत विवरण" />

              <div className="row g-2">
                <div className="col-md-6">
                  <Label en="Email" hi="ईमेल" />
                  <div className={`fr-field ${errors.email ? "fr-field--error" : ""}`}>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      {...register("email", {
                        required: "Email is required",
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address" },
                      })}
                    />
                  </div>
                  {errors.email && <p className="fr-error">{errors.email.message}</p>}
                </div>

                <div className="col-md-6">
                  <Label en="Full Name" hi="पूरा नाम" />
                  <div className={`fr-field ${errors.fullName ? "fr-field--error" : ""}`}>
                    <input
                      type="text"
                      placeholder="Enter your full name / अपना पूरा नाम दर्ज करें"
                      {...register("fullName", { required: "Full name is required" })}
                    />
                  </div>
                  {errors.fullName && <p className="fr-error">{errors.fullName.message}</p>}
                </div>

                <div className="col-md-6">
                  <Label en="Father / Husband Name" hi="पिता / पति का नाम" />
                  <div className="fr-field">
                    <input type="text" {...register("guardianName")} />
                  </div>
                </div>

                <div className="col-md-6">
                  <Label en="WhatsApp Mobile Number" hi="व्हाट्सएप मोबाइल नंबर" />
                  <div className={`fr-field ${errors.whatsappNumber ? "fr-field--error" : ""}`}>
                    <input
                      type="tel"
                      maxLength="10"
                      placeholder="10-digit number / 10 अंकों का नंबर"
                      {...register("whatsappNumber", {
                        required: "WhatsApp number is required",
                        pattern: { value: /^[6-9]\d{9}$/, message: "Enter a valid 10-digit number" },
                      })}
                    />
                  </div>
                  {errors.whatsappNumber && <p className="fr-error">{errors.whatsappNumber.message}</p>}
                </div>

                <div className="col-md-6">
                  <Label en="Date of Birth" hi="जन्म तिथि" />
                  <div className={`fr-field ${errors.dob ? "fr-field--error" : ""}`}>
                    <input type="date" {...register("dob", { required: "Date of birth is required" })} />
                  </div>
                  {errors.dob && <p className="fr-error">{errors.dob.message}</p>}
                </div>

                <div className="col-12">
                  <Label en="Gender" hi="लिंग" />
                  <OptionList
                    options={GENDERS}
                    name="gender"
                    register={register}
                    rules={{ required: "Please select a gender" }}
                  />
                  {errors.gender && <p className="fr-error">{errors.gender.message}</p>}
                </div>

                <div className="col-12">
                  <Label en="Category" hi="श्रेणी" />
                  <OptionList
                    options={CATEGORIES}
                    name="category"
                    register={register}
                    rules={{ required: "Please select a category" }}
                  />
                  {errors.category && <p className="fr-error">{errors.category.message}</p>}
                </div>

                <div className="col-12">
                  <Label en="State" hi="राज्य" />
                  <OptionList
                    options={STATES.map((s) => [s.split(" / ")[0], s.split(" / ")[1]])}
                    name="state"
                    register={register}
                    rules={{ required: "Please select a state" }}
                  />
                  {errors.state && <p className="fr-error">{errors.state.message}</p>}
                </div>

                <div className="col-md-6">
                  <Label en="District" hi="जिला" />
                  <div className={`fr-field ${errors.district ? "fr-field--error" : ""}`}>
                    <select disabled={!state} {...register("district", { required: "Please select your district" })}>
                      <option value="">{state ? "Select District / जिला चुनें" : "Select a state first"}</option>
                      {districts.map(([en, hi]) => (
                        <option value={`${en} / ${hi}`} key={en}>
                          {en} / {hi}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.district && <p className="fr-error">{errors.district.message}</p>}
                </div>

                <div className="col-md-6">
                  <Label en="Pin Code" hi="पिन कोड" />
                  <div className={`fr-field ${errors.pincode ? "fr-field--error" : ""}`}>
                    <input
                      type="text"
                      maxLength="6"
                      placeholder="6 digits / 6 अंक"
                      {...register("pincode", {
                        required: "Pin code is required",
                        pattern: { value: /^[1-9]\d{5}$/, message: "Enter a valid 6-digit pin code" },
                      })}
                    />
                  </div>
                  {errors.pincode && <p className="fr-error">{errors.pincode.message}</p>}
                </div>
              </div>
            </div>

            {/* SECTION 2 */}
            <div className="fr-section">
              <SectionHead n={2} en="Skills & Experience" hi="कौशल एवं अनुभव" />

              <p className="fr-label" style={{ marginBottom: "10px" }}>
                Which of the following skills/services do you have experience in?
                <span className="fr-label-hi"> / आपको निम्नलिखित में से किन कौशल/सेवाओं का अनुभव है?</span>
              </p>

              <OptionList options={SKILLS} name="skills" register={register} type="checkbox" grid />

              {skills.includes(OTHER_SKILL) && (
                <div style={{ marginTop: "10px" }}>
                  <Label
                    en='If you selected "Other", please briefly describe your experience'
                    hi='यदि आपने "अन्य" चुना है, तो अपने अनुभव के बारे में संक्षेप में बताएं'
                  />
                  <div className="fr-field">
                    <input type="text" placeholder="Short Answer / संक्षिप्त उत्तर" {...register("otherSkillDescription")} />
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 3 */}
            <div className="fr-section">
              <SectionHead n={3} en="Declaration" hi="घोषणा" />

              <div className="fr-declaration" style={{ marginBottom: "10px" }}>
                <p className="mb-2">
                  I declare that the information provided by me in this form is
                  true and correct to the best of my knowledge. I understand
                  that this form is an enquiry for the HKI Studio franchise
                  opportunity and that submission of this form does not
                  guarantee approval or franchise allocation.
                </p>
                <p className="mb-0">
                  मैं घोषणा करता/करती हूँ कि मेरे द्वारा इस फॉर्म में दी गई
                  जानकारी मेरी जानकारी के अनुसार सत्य एवं सही है। मैं समझता/समझती
                  हूँ कि यह फॉर्म HKI Studio फ्रैंचाइज़ी अवसर के संबंध में
                  पूछताछ के लिए है और इस फॉर्म को जमा करने से फ्रैंचाइज़ी की
                  स्वीकृति या आवंटन की गारंटी नहीं होती।
                </p>
              </div>

              <label className="fr-option">
                <input
                  type="checkbox"
                  {...register("declarationAgreed", { required: "You must agree to the declaration" })}
                />
                {DECLARATION_VALUE}
              </label>
              {errors.declarationAgreed && <p className="fr-error">{errors.declarationAgreed.message}</p>}
            </div>

            {success && (
              <p className="fr-alert fr-alert--success" style={{ marginTop: "16px" }}>
                Thank you! Your enquiry has been submitted. / धन्यवाद! आपकी पूछताछ सबमिट हो गई है।
              </p>
            )}

            {submitError && (
              <p className="fr-alert fr-alert--error" style={{ marginTop: "16px" }}>
                {submitError}
              </p>
            )}

            <button type="submit" className="fr-btn" style={{ marginTop: "16px" }} disabled={isSubmitting}>
              Submit Enquiry / पूछताछ सबमिट करें
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default FranchiseForm;
